import { useState } from "react";
import { useCalculateRoi } from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Calculator, PoundSterling, TrendingUp, AlertTriangle, ArrowRight } from "lucide-react";

export default function RoiCalculator() {
  const [headcount, setHeadcount] = useState(250);
  const [seniorPct, setSeniorPct] = useState(20);
  const [seniorAvgSalary, setSeniorAvgSalary] = useState(85000);
  const [seniorTurnoverPct, setSeniorTurnoverPct] = useState(15);

  const calculateRoi = useCalculateRoi();

  // Auto-calculate when values change
  const [result, setResult] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculate = async () => {
    setIsCalculating(true);
    try {
      const data = await calculateRoi.mutateAsync({
        data: {
          headcount,
          seniorPct,
          seniorAvgSalary,
          seniorTurnoverPct
        }
      });
      setResult(data);
    } catch (error) {
      console.error("Failed to calculate ROI", error);
    } finally {
      setIsCalculating(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ROI Calculator</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Model the financial impact of uncaptured knowledge when senior employees depart, and calculate the potential return on investment for formal knowledge transition processes.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 space-y-6">
          <Card className="border-border shadow-sm">
            <CardHeader className="bg-muted/30 border-b pb-4">
              <CardTitle className="text-lg flex items-center">
                <Calculator className="mr-2 h-5 w-5 text-primary" />
                Company Parameters
              </CardTitle>
              <CardDescription>Adjust the sliders to match your organisation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8 pt-6">
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">Total Headcount</label>
                  <span className="font-mono text-sm bg-muted px-2 py-1 rounded">{headcount}</span>
                </div>
                <Slider 
                  value={[headcount]} 
                  onValueChange={(v) => setHeadcount(v[0])} 
                  max={1000} 
                  min={50} 
                  step={10} 
                />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">Senior Employees (%)</label>
                  <span className="font-mono text-sm bg-muted px-2 py-1 rounded">{seniorPct}%</span>
                </div>
                <Slider 
                  value={[seniorPct]} 
                  onValueChange={(v) => setSeniorPct(v[0])} 
                  max={50} 
                  min={5} 
                  step={1} 
                />
                <p className="text-xs text-muted-foreground text-right">{Math.round(headcount * (seniorPct / 100))} senior staff</p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">Avg Senior Salary</label>
                  <span className="font-mono text-sm bg-muted px-2 py-1 rounded">{formatCurrency(seniorAvgSalary)}</span>
                </div>
                <Slider 
                  value={[seniorAvgSalary]} 
                  onValueChange={(v) => setSeniorAvgSalary(v[0])} 
                  max={200000} 
                  min={40000} 
                  step={5000} 
                />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium">Annual Turnover (Senior)</label>
                  <span className="font-mono text-sm bg-muted px-2 py-1 rounded">{seniorTurnoverPct}%</span>
                </div>
                <Slider 
                  value={[seniorTurnoverPct]} 
                  onValueChange={(v) => setSeniorTurnoverPct(v[0])} 
                  max={30} 
                  min={5} 
                  step={1} 
                />
                <p className="text-xs text-muted-foreground text-right">{Math.round(headcount * (seniorPct / 100) * (seniorTurnoverPct / 100))} departures per year</p>
              </div>
            </CardContent>
            <CardFooter className="bg-muted/30 border-t pt-4">
              <Button onClick={handleCalculate} className="w-full" size="lg" disabled={isCalculating}>
                {isCalculating ? "Calculating..." : "Calculate ROI"}
                {!isCalculating && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="md:col-span-7">
          {result ? (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <Card className="border-primary/20 bg-primary/5 shadow-md">
                <CardContent className="p-8">
                  <div className="text-center space-y-2">
                    <p className="text-sm font-semibold tracking-wider text-primary uppercase">Projected Annual Savings</p>
                    <div className="text-5xl font-bold tracking-tight text-foreground">
                      {formatCurrency(result.totalSavings)}
                    </div>
                    <p className="text-muted-foreground pt-2 flex items-center justify-center">
                      <TrendingUp className="mr-2 h-4 w-4 text-emerald-500" />
                      Net benefit: <strong className="ml-1 text-foreground">{formatCurrency(result.netBenefit)}</strong>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="grid sm:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col space-y-1">
                      <span className="text-sm text-muted-foreground">Total Cost of Knowledge Loss</span>
                      <span className="text-2xl font-semibold">{formatCurrency(result.totalAnnualCost)}</span>
                      <span className="text-xs text-muted-foreground pt-1">Across {result.seniorDepartures} senior departures</span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col space-y-1">
                      <span className="text-sm text-muted-foreground">Cost Per Unmanaged Exit</span>
                      <span className="text-2xl font-semibold">{formatCurrency(result.costPerSeniorExit)}</span>
                      <span className="text-xs text-muted-foreground pt-1">Based on productivity dip</span>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex flex-col space-y-1">
                      <span className="text-sm text-muted-foreground">Relay Engagement Cost</span>
                      <span className="text-2xl font-semibold">{formatCurrency(result.annualSpend)}</span>
                      <span className="text-xs text-muted-foreground pt-1">For {result.usableTransitions} managed transitions</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-primary text-primary-foreground">
                  <CardContent className="p-6">
                    <div className="flex flex-col space-y-1">
                      <span className="text-sm text-primary-foreground/80">Return on Investment</span>
                      <span className="text-3xl font-bold">{result.roi.toFixed(0)}%</span>
                      <span className="text-xs text-primary-foreground/80 pt-1">First year impact</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4 flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">The hidden cost of turnover</p>
                    <p className="text-muted-foreground mt-1">
                      When a senior employee leaves, their replacement typically takes 6-9 months to reach full productivity. A structured knowledge transfer reduces this ramp-up time by an average of 40%, directly translating to recovered salary value.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 border-2 border-dashed rounded-xl border-muted bg-muted/10">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <PoundSterling className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-medium">Ready to calculate</h3>
              <p className="text-muted-foreground max-w-sm mt-2">
                Adjust the parameters on the left to match your organisation, then click Calculate to see the financial impact.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
